export const ESTADOS = {
  gravando: { rotulo: "Gravando", cor: "success" },
  conectando: { rotulo: "Conectando", cor: "processing" },
  sem_sinal: {
    rotulo: "Sem sinal",
    cor: "error",
    frase: "A câmera ou o DVR não está enviando imagem para o gateway.",
  },
  erro_autenticacao: {
    rotulo: "Senha recusada",
    cor: "error",
    frase: "A câmera recusou o usuário ou a senha do endereço.",
  },
  erro_configuracao: {
    rotulo: "Endereço inválido",
    cor: "error",
    frase: "O endereço da câmera não está certo.",
  },
  fora_de_horario: { rotulo: "Fora do horário", cor: "default" },
  parada: { rotulo: "Parada", cor: "default" },
  desativada: { rotulo: "Desativada", cor: "default" },
};

export const AVISOS = {
  bitrate_alto:
    "Vídeo pesado. Configure o substream da câmera para gastar menos disco e internet.",
};

export const LIMITE_KBPS_RECOMENDADO = 1000;

export function estadoDaCamera(reportada) {
  if (!reportada?.estado) {
    return { rotulo: "Sem informação", cor: "default" };
  }
  return (
    ESTADOS[reportada.estado] || { rotulo: reportada.estado, cor: "default" }
  );
}

export function mascararEndereco(endereco) {
  if (!endereco) return "-";
  return endereco.replace(/(\/\/[^:/@]+):[^@/]*@/, "$1:••••@");
}

function paraData(valor) {
  if (!valor) return null;
  const data = valor instanceof Date ? valor : new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data;
}

function doisDigitos(n) {
  return String(n).padStart(2, "0");
}

export function hora(valor) {
  const data = paraData(valor);
  if (!data) return "-";
  return `${doisDigitos(data.getHours())}:${doisDigitos(data.getMinutes())}`;
}

export function diaEHora(valor, agora = new Date()) {
  const data = paraData(valor);
  if (!data) return "-";
  if (data.toDateString() === agora.toDateString()) return hora(data);
  return `${doisDigitos(data.getDate())}/${doisDigitos(
    data.getMonth() + 1
  )} às ${hora(data)}`;
}

export function haQuantoTempo(valor, agora = new Date()) {
  const data = paraData(valor);
  if (!data) return "-";
  const segundos = Math.max(0, Math.round((agora - data) / 1000));
  if (segundos < 60) return `há ${segundos} s`;
  const minutos = Math.floor(segundos / 60);
  if (minutos < 60) return `há ${minutos} min`;
  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;
  return `há ${Math.floor(horas / 24)} d`;
}

export function textoDaDecisao(decisao, agora = new Date()) {
  if (!decisao?.decididoEm) return "Sem parada manual";
  const onde = decisao.origem === "local" ? "no gateway" : "no Ouran";
  const acao = decisao.parada ? "Parada" : "Retomada";
  return `${acao} ${onde} ${
    diaEHora(decisao.decididoEm, agora).includes("/") ? "em" : "às"
  } ${diaEHora(decisao.decididoEm, agora)}`;
}

export function fraseDoGateway(estado, agora = new Date()) {
  const gateway = estado?.gateway;
  if (!gateway) return { texto: "-", cor: "default" };
  switch (gateway.situacao) {
    case "sem_gateway":
      return { texto: "Nenhum gateway conectado", cor: "default" };
    case "aguardando_registro":
      return {
        texto: "Token gerado, aguardando o gateway se registrar",
        cor: "processing",
      };
    case "silencioso":
      return {
        texto: `Silencioso desde ${diaEHora(gateway.ultimoHeartbeatEm, agora)}`,
        cor: "error",
      };
    default:
      return {
        texto: `Online, heartbeat ${haQuantoTempo(
          gateway.ultimoHeartbeatEm,
          agora
        )}`,
        cor: "success",
      };
  }
}

export function resumoDasCameras(cameras) {
  const lista = cameras || [];
  const gravando = lista.filter(
    (c) => c.reportada?.estado === "gravando"
  ).length;
  return `${gravando} de ${lista.length} câmeras gravando`;
}

export function batidasPorMinuto(historico, agora = new Date(), minutos = 30) {
  const recebidos = new Set(
    (historico || [])
      .map((h) => paraData(h.em))
      .filter(Boolean)
      .map((d) => Math.floor(d.getTime() / 60000))
  );
  const atual = Math.floor(agora.getTime() / 60000);
  const faixa = [];
  for (let i = minutos - 1; i >= 0; i -= 1) {
    const minuto = atual - i;
    faixa.push({
      em: new Date(minuto * 60000),
      recebido: recebidos.has(minuto),
    });
  }
  return faixa;
}

export function partesDoDisco(disco) {
  const total = Number(disco?.totalGb) || 0;
  const livre = Number(disco?.livreGb) || 0;
  const buffer = Number(disco?.bufferGb) || 0;
  if (total <= 0) return null;
  const outros = Math.max(0, total - livre - buffer);
  return {
    total,
    percentualLivre: Math.round((livre / total) * 100),
    partes: [
      { nome: "Vídeos do Ouran", valor: buffer },
      { nome: "Outros arquivos", valor: Math.round(outros * 10) / 10 },
      { nome: "Livre", valor: livre },
    ],
  };
}

export function segundosDeDesvio(desvioMs) {
  if (desvioMs === null || desvioMs === undefined) return null;
  return Math.round(desvioMs / 100) / 10;
}

export function faixasDeSegmentos(segmentos, agora = new Date(), horas = 6) {
  const passo = 5 * 60000;
  const fim = agora.getTime();
  const inicio = fim - horas * 3600000;
  const total = Math.round((fim - inicio) / passo);
  const intervalos = (segmentos || [])
    .map((s) => [paraData(s.inicio), paraData(s.fim)])
    .filter(([a, b]) => a && b)
    .map(([a, b]) => [a.getTime(), b.getTime()]);
  const faixas = [];
  for (let i = 0; i < total; i += 1) {
    const a = inicio + i * passo;
    const b = a + passo;
    faixas.push({
      em: new Date(a),
      recebido: intervalos.some(([x, y]) => x < b && y > a),
    });
  }
  return faixas;
}

export function descreverErro(erro, alternativa) {
  const dados = erro?.response?.data;
  if (!dados) return erro?.message || alternativa;
  if (typeof dados === "string") return alternativa;
  if (dados.erro) return dados.erro;
  if (dados.detail) return dados.detail;
  return Object.entries(dados)
    .map(([campo, mensagens]) => `${campo}: ${[].concat(mensagens).join(" ")}`)
    .join(" | ");
}
